import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext.tsx';
import { useRealtime } from './RealtimeContext.tsx';
import {
  startOutgoingCallerRingtone,
  stopOutgoingCallerRingtone,
  startIncomingCalleeRingtone,
  stopIncomingCalleeRingtone,
  stopAllCallRingtones,
} from '../services/soundEffects.ts';

export interface CallParticipant {
  id: string;
  name: string;
  role: string;
  avatarUrl: string;
  isMuted: boolean;
  isVideoOff: boolean;
  isSpeaking: boolean;
  isScreenSharing?: boolean;
}

export interface ActiveCallState {
  callId: string;
  type: 'direct' | 'group';
  callMode?: 'audio' | 'video';
  title: string;
  channelId?: string;
  projectId?: string;
  targetUserId?: string;
  isAudioMuted: boolean;
  isVideoOff: boolean;
  isScreenSharing: boolean;
  isFloating: boolean; // PiP mode vs Full/Expanded Modal
  participants: CallParticipant[];
  startedAt: number;
}

export interface IncomingCallInfo {
  callId: string;
  type: 'direct' | 'group';
  callMode?: 'audio' | 'video';
  title: string;
  callerId: string;
  callerName: string;
  callerAvatar?: string;
  callerRole?: string;
  channelId?: string;
}

interface CallContextType {
  activeCall: ActiveCallState | null;
  incomingCall: IncomingCallInfo | null;
  localStream: MediaStream | null;
  screenStream: MediaStream | null;
  remoteStreams: Record<string, MediaStream>;
  hasCamera: boolean;
  hasMic: boolean;
  isOutgoingRinging: boolean;
  startDirectCall: (
    targetUser: { id: string; name: string; avatarUrl?: string; role?: string; title?: string },
    withVideo?: boolean,
    channelId?: string
  ) => Promise<void>;
  startGroupCall: (
    channel: { id: string; name: string; projectId?: string },
    withVideo?: boolean
  ) => Promise<void>;
  joinCall: (callState: Partial<ActiveCallState>) => Promise<void>;
  acceptIncomingCall: (withVideo?: boolean) => Promise<void>;
  declineIncomingCall: () => void;
  endCall: () => void;
  toggleAudio: () => void;
  toggleVideo: () => void;
  toggleScreenShare: () => Promise<void>;
  toggleFloating: () => void;
}

const CallContext = createContext<CallContextType>({
  activeCall: null,
  incomingCall: null,
  localStream: null,
  screenStream: null,
  remoteStreams: {},
  hasCamera: false,
  hasMic: false,
  isOutgoingRinging: false,
  startDirectCall: async () => {},
  startGroupCall: async () => {},
  joinCall: async () => {},
  acceptIncomingCall: async () => {},
  declineIncomingCall: () => {},
  endCall: () => {},
  toggleAudio: () => {},
  toggleVideo: () => {},
  toggleScreenShare: async () => {},
  toggleFloating: () => {},
});

function formatParticipantRole(u?: { title?: string | null; role?: string | null } | null): string {
  if (u?.title && !/Chief Tech(nology|nical) Officer/i.test(u.title)) {
    return u.title;
  }
  const rawRole = (u?.role || '').trim();
  if (!rawRole) return 'Team Member';
  if (/Chief Tech(nology|nical) Officer/i.test(rawRole)) return 'Administrator';
  const lower = rawRole.toLowerCase();
  if (lower === 'admin') return 'Administrator';
  if (lower === 'manager') return 'Manager';
  if (lower === 'team_lead') return 'Team Lead';
  if (lower === 'member') return 'Team Member';
  if (lower === 'viewer') return 'Viewer';
  if (lower === 'guest') return 'Guest';
  return rawRole;
}

export const CallProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, token } = useAuth();
  const { sendCallSignal, subscribe } = useRealtime();

  const [activeCall, setActiveCallState] = useState<ActiveCallState | null>(null);
  const [incomingCall, setIncomingCall] = useState<IncomingCallInfo | null>(null);
  const [isOutgoingRinging, setIsOutgoingRinging] = useState(false);

  // Synchronized activeCallRef to avoid stale closures in realtime handlers
  const activeCallRef = useRef<ActiveCallState | null>(null);
  const setActiveCall = (valOrFn: ActiveCallState | null | ((prev: ActiveCallState | null) => ActiveCallState | null)) => {
    setActiveCallState((prev) => {
      const next = typeof valOrFn === 'function' ? (valOrFn as any)(prev) : valOrFn;
      activeCallRef.current = next;
      return next;
    });
  };

  // Live media streams
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [screenStream, setScreenStream] = useState<MediaStream | null>(null);
  const [remoteStreams, setRemoteStreams] = useState<Record<string, MediaStream>>({});
  const [hasCamera, setHasCamera] = useState<boolean>(false);
  const [hasMic, setHasMic] = useState<boolean>(false);

  const localStreamRef = useRef<MediaStream | null>(null);
  const screenStreamRef = useRef<MediaStream | null>(null);
  const peerConnectionsRef = useRef<Map<string, RTCPeerConnection>>(new Map());
  const pendingIceCandidatesRef = useRef<Map<string, RTCIceCandidateInit[]>>(new Map());

  // Buffer ICE candidates until remote description is settled
  const queueIceCandidate = (peerId: string, candidate: RTCIceCandidateInit) => {
    if (!pendingIceCandidatesRef.current.has(peerId)) {
      pendingIceCandidatesRef.current.set(peerId, []);
    }
    pendingIceCandidatesRef.current.get(peerId)!.push(candidate);
  };

  const processPendingIceCandidates = async (peerId: string, pc: RTCPeerConnection) => {
    const queue = pendingIceCandidatesRef.current.get(peerId);
    if (queue && queue.length > 0) {
      while (queue.length > 0) {
        const cand = queue.shift();
        if (cand) {
          try {
            await pc.addIceCandidate(new RTCIceCandidate(cand));
          } catch (e) {
            console.warn('[WebRTC] addIceCandidate error:', e);
          }
        }
      }
    }
  };

  // Acquire local media helper
  const acquireMedia = async (withVideo: boolean = true): Promise<MediaStream | null> => {
    let stream: MediaStream | null = null;
    let camFound = false;
    let micFound = false;

    if (typeof navigator !== 'undefined' && navigator.mediaDevices?.getUserMedia) {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: withVideo ? { width: { ideal: 1280 }, height: { ideal: 720 } } : false,
          audio: { echoCancellation: true, noiseSuppression: true },
        });
        camFound = withVideo && stream.getVideoTracks().length > 0;
        micFound = stream.getAudioTracks().length > 0;
      } catch (videoErr) {
        // Fallback: try audio only if camera is not available or denied
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: false,
            audio: { echoCancellation: true, noiseSuppression: true },
          });
          micFound = stream.getAudioTracks().length > 0;
        } catch (audioErr) {
          // Both media devices unavailable or denied
          stream = null;
        }
      }
    }

    localStreamRef.current = stream;
    setLocalStream(stream);
    setHasCamera(camFound);
    setHasMic(micFound);
    return stream;
  };

  // Real voice audio analyzer: detects true speaking activity from microphone
  useEffect(() => {
    if (!localStream) return;
    const audioTracks = localStream.getAudioTracks();
    if (audioTracks.length === 0) return;

    let audioCtx: AudioContext | null = null;
    let analyser: AnalyserNode | null = null;
    let source: MediaStreamAudioSourceNode | null = null;
    let animId: number;

    try {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      if (AudioContextClass) {
        audioCtx = new AudioContextClass();
        analyser = audioCtx.createAnalyser();
        analyser.fftSize = 256;
        source = audioCtx.createMediaStreamSource(localStream);
        source.connect(analyser);

        const dataArray = new Uint8Array(analyser.frequencyBinCount);

        const checkVolume = () => {
          if (!analyser) return;
          analyser.getByteFrequencyData(dataArray);
          let sum = 0;
          for (let i = 0; i < dataArray.length; i++) {
            sum += dataArray[i];
          }
          const avg = sum / dataArray.length;
          const isSpeakingNow = avg > 16;

          setActiveCall((prev) => {
            if (!prev) return null;
            const myId = user?.id || 'usr-admin';
            const updated = prev.participants.map((p) =>
              p.id === myId ? { ...p, isSpeaking: !prev.isAudioMuted && isSpeakingNow } : p
            );
            return { ...prev, participants: updated };
          });

          animId = requestAnimationFrame(checkVolume);
        };

        animId = requestAnimationFrame(checkVolume);
      }
    } catch (e) {}

    return () => {
      if (animId) cancelAnimationFrame(animId);
      if (source) source.disconnect();
      if (audioCtx && audioCtx.state !== 'closed') audioCtx.close().catch(() => {});
    };
  }, [localStream, user?.id]);

  // WebRTC Peer Connection Helper
  const createPeerConnection = (peerId: string) => {
    if (peerConnectionsRef.current.has(peerId)) {
      const existingPc = peerConnectionsRef.current.get(peerId)!;
      if (localStreamRef.current) {
        const senders = existingPc.getSenders();
        localStreamRef.current.getTracks().forEach((track) => {
          if (!senders.some((s) => s.track?.id === track.id)) {
            existingPc.addTrack(track, localStreamRef.current!);
          }
        });
      }
      return existingPc;
    }

    const pc = new RTCPeerConnection({
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' },
        { urls: 'stun:stun2.l.google.com:19302' },
        { urls: 'stun:stun3.l.google.com:19302' },
        { urls: 'stun:stun4.l.google.com:19302' },
      ],
    });

    // Add local tracks to peer connection
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => {
        pc.addTrack(track, localStreamRef.current!);
      });
    }

    pc.onicecandidate = (event) => {
      if (event.candidate) {
        sendCallSignal({
          type: 'call_signal',
          action: 'webrtc_ice',
          candidate: event.candidate,
          targetUserId: peerId,
        });
      }
    };

    pc.ontrack = (event) => {
      // Set zero latency delay hint to eliminate audio/video drift
      try {
        if (event.receiver && 'playoutDelayHint' in event.receiver) {
          (event.receiver as any).playoutDelayHint = 0;
        }
      } catch (e) {}

      setRemoteStreams((prev) => {
        const existingStream = prev[peerId];
        if (existingStream) {
          if (!existingStream.getTracks().some((t) => t.id === event.track.id)) {
            existingStream.addTrack(event.track);
          }
          // Produce a fresh MediaStream reference containing all tracks to ensure React triggers full re-render
          return { ...prev, [peerId]: new MediaStream(existingStream.getTracks()) };
        } else {
          const initialTracks = event.streams && event.streams[0]
            ? event.streams[0].getTracks()
            : [event.track];
          return { ...prev, [peerId]: new MediaStream(initialTracks) };
        }
      });
    };

    peerConnectionsRef.current.set(peerId, pc);
    return pc;
  };

  // Stop outgoing caller ringtone automatically once any remote media stream arrives
  useEffect(() => {
    if (Object.keys(remoteStreams).length > 0 && isOutgoingRinging) {
      setIsOutgoingRinging(false);
      stopOutgoingCallerRingtone();
    }
  }, [remoteStreams, isOutgoingRinging]);

  // Subscribe to realtime call signals from peers
  useEffect(() => {
    const unsubscribe = subscribe(async (event) => {
      if (event.type === 'call_signal') {
        const signal = event.data;
        const currentCall = activeCallRef.current;

        // Incoming call received
        if (signal.action === 'incoming_call') {
          // If we are not currently on a call, present incoming call prompt and play incoming ringtone
          if (!currentCall) {
            const mode: 'audio' | 'video' = signal.callMode === 'audio' ? 'audio' : 'video';
            startIncomingCalleeRingtone();
            setIncomingCall({
              callId: signal.callId || `call_${Date.now()}`,
              type: signal.callType || 'direct',
              callMode: mode,
              title:
                signal.title ||
                `${mode === 'audio' ? 'Audio' : 'Video'} Call from ${signal.callerName || 'Team Member'}`,
              callerId: signal.fromUserId,
              callerName: signal.callerName || 'Team Member',
              callerAvatar: signal.callerAvatar || '',
              callerRole: formatParticipantRole({ role: signal.callerRole }),
              channelId: signal.channelId,
            });
          }
        } else if (signal.action === 'call_accepted') {
          // Peer accepted our call -> stop outgoing ringback tone and initiate WebRTC offer
          setIsOutgoingRinging(false);
          stopOutgoingCallerRingtone();

          if (currentCall && (currentCall.targetUserId === signal.fromUserId || !currentCall.targetUserId)) {
            if (signal.withVideo !== undefined) {
              setActiveCall((prev) => {
                if (!prev) return null;
                return {
                  ...prev,
                  participants: prev.participants.map((p) =>
                    p.id === signal.fromUserId ? { ...p, isVideoOff: !signal.withVideo } : p
                  ),
                };
              });
            }

            try {
              const pc = createPeerConnection(signal.fromUserId);
              if (localStreamRef.current) {
                const senders = pc.getSenders();
                localStreamRef.current.getTracks().forEach((track) => {
                  if (!senders.some((s) => s.track?.id === track.id)) {
                    pc.addTrack(track, localStreamRef.current!);
                  }
                });
              }
              const offer = await pc.createOffer();
              await pc.setLocalDescription(offer);
              sendCallSignal({
                type: 'call_signal',
                action: 'webrtc_offer',
                sdp: offer,
                targetUserId: signal.fromUserId,
              });
            } catch (err) {
              console.warn('WebRTC offer negotiation error:', err);
            }
          }
        } else if (signal.action === 'call_declined') {
          // Peer declined -> stop ringtones and close direct call
          setIsOutgoingRinging(false);
          stopAllCallRingtones();
          if (currentCall && currentCall.targetUserId === signal.fromUserId) {
            endCall();
          }
        } else if (signal.action === 'webrtc_offer') {
          // Callee (or renegotiating peer) receives WebRTC offer -> create answer
          setIsOutgoingRinging(false);
          stopAllCallRingtones();
          try {
            const pc = createPeerConnection(signal.fromUserId);
            if (localStreamRef.current) {
              const senders = pc.getSenders();
              localStreamRef.current.getTracks().forEach((track) => {
                if (!senders.some((s) => s.track?.id === track.id)) {
                  pc.addTrack(track, localStreamRef.current!);
                }
              });
            }
            await pc.setRemoteDescription(new RTCSessionDescription(signal.sdp));
            await processPendingIceCandidates(signal.fromUserId, pc);
            const answer = await pc.createAnswer();
            await pc.setLocalDescription(answer);
            sendCallSignal({
              type: 'call_signal',
              action: 'webrtc_answer',
              sdp: answer,
              targetUserId: signal.fromUserId,
            });
          } catch (err) {
            console.warn('WebRTC answer error:', err);
          }
        } else if (signal.action === 'webrtc_answer') {
          // Caller receives WebRTC answer
          setIsOutgoingRinging(false);
          stopOutgoingCallerRingtone();
          try {
            const pc = peerConnectionsRef.current.get(signal.fromUserId);
            if (pc) {
              await pc.setRemoteDescription(new RTCSessionDescription(signal.sdp));
              await processPendingIceCandidates(signal.fromUserId, pc);
            }
          } catch (err) {
            console.warn('WebRTC setRemoteDescription error:', err);
          }
        } else if (signal.action === 'webrtc_ice') {
          // ICE candidate exchange
          try {
            const pc = peerConnectionsRef.current.get(signal.fromUserId);
            if (pc && pc.remoteDescription && pc.remoteDescription.type) {
              await pc.addIceCandidate(new RTCIceCandidate(signal.candidate));
            } else {
              queueIceCandidate(signal.fromUserId, signal.candidate);
            }
          } catch (err) {
            console.warn('Error handling ICE candidate:', err);
          }
        } else if (signal.action === 'media_state_change') {
          // Peer toggled video/audio
          setActiveCall((prev) => {
            if (!prev) return null;
            const updated = prev.participants.map((p) => {
              if (p.id === signal.userId) {
                return {
                  ...p,
                  isMuted: signal.isMuted !== undefined ? signal.isMuted : p.isMuted,
                  isVideoOff: signal.isVideoOff !== undefined ? signal.isVideoOff : p.isVideoOff,
                  isScreenSharing: signal.isScreenSharing !== undefined ? signal.isScreenSharing : p.isScreenSharing,
                };
              }
              return p;
            });
            return { ...prev, participants: updated };
          });
        } else if (signal.action === 'screen_share_change') {
          // Peer toggled screen share
          setActiveCall((prev) => {
            if (!prev) return null;
            const updated = prev.participants.map((p) => {
              if (p.id === signal.userId) {
                return {
                  ...p,
                  isScreenSharing: Boolean(signal.isScreenSharing),
                };
              }
              return p;
            });
            return {
              ...prev,
              participants: updated,
            };
          });
        } else if (signal.action === 'call_ended') {
          // Call ended by peer
          setIsOutgoingRinging(false);
          stopAllCallRingtones();
          cleanupMedia();
          setActiveCall(null);
          setIncomingCall(null);
        }
      }
    });

    return unsubscribe;
  }, [subscribe, user?.id]);

  const cleanupMedia = () => {
    stopAllCallRingtones();
    setIsOutgoingRinging(false);
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => track.stop());
      localStreamRef.current = null;
      setLocalStream(null);
    }
    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach((track) => track.stop());
      screenStreamRef.current = null;
      setScreenStream(null);
    }
    peerConnectionsRef.current.forEach((pc) => pc.close());
    peerConnectionsRef.current.clear();
    pendingIceCandidatesRef.current.clear();
    setRemoteStreams({});
  };

  // Ensure a DM channel exists for a 1-on-1 call so in-call chat & file sharing works seamlessly
  const ensureDirectChannelId = async (targetUserId: string, existingChannelId?: string): Promise<string | undefined> => {
    if (existingChannelId) return existingChannelId;
    const activeToken = token || localStorage.getItem('nexus_auth_token') || '';
    if (!activeToken || !targetUserId) return undefined;
    try {
      const res = await fetch('/api/chat/channels', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${activeToken}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
        body: JSON.stringify({ type: 'direct', targetUserId }),
      });
      if (res.ok) {
        const data = await res.json();
        return data.channelId;
      }
    } catch {}
    return undefined;
  };

  const startDirectCall = async (
    targetUser: { id: string; name: string; avatarUrl?: string; role?: string; title?: string },
    withVideo: boolean = true,
    channelId?: string
  ) => {
    const stream = await acquireMedia(withVideo);
    const resolvedChannelId = await ensureDirectChannelId(targetUser.id, channelId);

    const myParticipant: CallParticipant = {
      id: user?.id || 'usr-admin',
      name: user?.fullName || 'Team Member',
      role: formatParticipantRole(user),
      avatarUrl: user?.avatarUrl || '',
      isMuted: false,
      isVideoOff: !withVideo || !stream || stream.getVideoTracks().length === 0,
      isSpeaking: false,
    };

    const peerParticipant: CallParticipant = {
      id: targetUser.id,
      name: targetUser.name,
      role: formatParticipantRole({ title: targetUser.title, role: targetUser.role }),
      avatarUrl: targetUser.avatarUrl || '',
      isMuted: false,
      isVideoOff: !withVideo,
      isSpeaking: false,
    };

    const callId = `call_direct_${Date.now()}`;
    const callMode: 'audio' | 'video' = withVideo ? 'video' : 'audio';

    setIsOutgoingRinging(true);
    startOutgoingCallerRingtone();

    setActiveCall({
      callId,
      type: 'direct',
      callMode,
      title: `${withVideo ? 'Video' : 'Audio'} Call with ${targetUser.name}`,
      channelId: resolvedChannelId,
      targetUserId: targetUser.id,
      isAudioMuted: false,
      isVideoOff: !withVideo || !stream || stream.getVideoTracks().length === 0,
      isScreenSharing: false,
      isFloating: false,
      participants: [myParticipant, peerParticipant],
      startedAt: Date.now(),
    });

    sendCallSignal({
      type: 'call_signal',
      action: 'incoming_call',
      callId,
      callType: 'direct',
      callMode,
      channelId: resolvedChannelId,
      title: `${withVideo ? 'Video' : 'Audio'} Call with ${user?.fullName || 'Team Member'}`,
      targetUserId: targetUser.id,
      callerName: user?.fullName || 'Team Member',
      callerAvatar: user?.avatarUrl || '',
      callerRole: formatParticipantRole(user),
    });
  };

  const startGroupCall = async (
    channel: { id: string; name: string; projectId?: string },
    withVideo: boolean = true
  ) => {
    const stream = await acquireMedia(withVideo);

    const myParticipant: CallParticipant = {
      id: user?.id || 'usr-admin',
      name: user?.fullName || 'Team Member',
      role: formatParticipantRole(user),
      avatarUrl: user?.avatarUrl || '',
      isMuted: false,
      isVideoOff: !withVideo || !stream || stream.getVideoTracks().length === 0,
      isSpeaking: false,
    };

    // Dynamically query channel participants from server
    let groupParticipants: CallParticipant[] = [myParticipant];
    try {
      const activeToken = token || localStorage.getItem('nexus_auth_token') || '';
      const res = await fetch('/api/chat/channels', {
        headers: {
          ...(activeToken ? { Authorization: `Bearer ${activeToken}` } : {}),
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
      });
      if (res.ok) {
        const data = await res.json();
        const foundChan = (data.channels || []).find((c: any) => c.id === channel.id);
        if (foundChan && Array.isArray(foundChan.members)) {
          const others = foundChan.members
            .filter((p: any) => p.id !== user?.id)
            .map((p: any) => ({
              id: p.id,
              name: p.fullName || p.name || 'Team Member',
              role: formatParticipantRole({ title: p.title, role: p.role }),
              avatarUrl: p.avatarUrl || '',
              isMuted: false,
              isVideoOff: !withVideo,
              isSpeaking: false,
            }));
          groupParticipants = [myParticipant, ...others];
        }
      }
    } catch (e) {}

    const callId = `call_group_${Date.now()}`;
    const callMode: 'audio' | 'video' = withVideo ? 'video' : 'audio';

    setIsOutgoingRinging(true);
    startOutgoingCallerRingtone();

    setActiveCall({
      callId,
      type: 'group',
      callMode,
      title: `#${channel.name} ${withVideo ? 'Video' : 'Audio'} Call`,
      channelId: channel.id,
      projectId: channel.projectId,
      isAudioMuted: false,
      isVideoOff: !withVideo || !stream || stream.getVideoTracks().length === 0,
      isScreenSharing: false,
      isFloating: false,
      participants: groupParticipants,
      startedAt: Date.now(),
    });

    sendCallSignal({
      type: 'call_signal',
      action: 'incoming_call',
      callId,
      callType: 'group',
      callMode,
      title: `#${channel.name} ${withVideo ? 'Video' : 'Audio'} Call`,
      channelId: channel.id,
      callerName: user?.fullName || 'Team Member',
      callerAvatar: user?.avatarUrl || '',
      callerRole: formatParticipantRole(user),
    });
  };

  const joinCall = async (callState: Partial<ActiveCallState>) => {
    const withVideo = callState.callMode !== 'audio';
    const stream = await acquireMedia(withVideo);

    const myParticipant: CallParticipant = {
      id: user?.id || 'usr-admin',
      name: user?.fullName || 'Team Member',
      role: formatParticipantRole(user),
      avatarUrl: user?.avatarUrl || '',
      isMuted: false,
      isVideoOff: !withVideo || !stream || stream.getVideoTracks().length === 0,
      isSpeaking: false,
    };

    setActiveCall({
      callId: callState.callId || `call_${Date.now()}`,
      type: callState.type || 'group',
      callMode: callState.callMode || 'video',
      title: callState.title || 'Live Conference',
      channelId: callState.channelId,
      projectId: callState.projectId,
      targetUserId: callState.targetUserId,
      isAudioMuted: false,
      isVideoOff: !withVideo || !stream || stream.getVideoTracks().length === 0,
      isScreenSharing: false,
      isFloating: false,
      participants: callState.participants || [myParticipant],
      startedAt: Date.now(),
    });
  };

  const acceptIncomingCall = async (withVideo?: boolean) => {
    if (!incomingCall) return;

    // Stop incoming ringtone immediately when accepted
    stopIncomingCalleeRingtone();

    const shouldStartWithVideo =
      typeof withVideo === 'boolean' ? withVideo : incomingCall.callMode !== 'audio';

    const stream = await acquireMedia(shouldStartWithVideo);
    const resolvedChannelId =
      incomingCall.channelId ||
      (incomingCall.type === 'direct'
        ? await ensureDirectChannelId(incomingCall.callerId, incomingCall.channelId)
        : undefined);

    const myParticipant: CallParticipant = {
      id: user?.id || 'usr-admin',
      name: user?.fullName || 'Team Member',
      role: formatParticipantRole(user),
      avatarUrl: user?.avatarUrl || '',
      isMuted: false,
      isVideoOff: !shouldStartWithVideo || !stream || stream.getVideoTracks().length === 0,
      isSpeaking: false,
    };

    const callerParticipant: CallParticipant = {
      id: incomingCall.callerId,
      name: incomingCall.callerName,
      role: formatParticipantRole({ role: incomingCall.callerRole }),
      avatarUrl: incomingCall.callerAvatar || '',
      isMuted: false,
      isVideoOff: incomingCall.callMode === 'audio',
      isSpeaking: false,
    };

    setActiveCall({
      callId: incomingCall.callId,
      type: incomingCall.type,
      callMode: shouldStartWithVideo ? 'video' : 'audio',
      title: incomingCall.title,
      channelId: resolvedChannelId,
      targetUserId: incomingCall.callerId,
      isAudioMuted: false,
      isVideoOff: !shouldStartWithVideo || !stream || stream.getVideoTracks().length === 0,
      isScreenSharing: false,
      isFloating: false,
      participants: [callerParticipant, myParticipant],
      startedAt: Date.now(),
    });

    // Initialize peer connection and attach local media immediately so candidate arrival won't be dropped
    const pc = createPeerConnection(incomingCall.callerId);
    if (stream) {
      const senders = pc.getSenders();
      stream.getTracks().forEach((track) => {
        if (!senders.some((s) => s.track?.id === track.id)) {
          pc.addTrack(track, stream);
        }
      });
    }

    sendCallSignal({
      type: 'call_signal',
      action: 'call_accepted',
      callId: incomingCall.callId,
      withVideo: shouldStartWithVideo,
      targetUserId: incomingCall.callerId,
      channelId: resolvedChannelId,
    });

    setIncomingCall(null);
  };

  const declineIncomingCall = () => {
    stopIncomingCalleeRingtone();
    if (incomingCall) {
      sendCallSignal({
        type: 'call_signal',
        action: 'call_declined',
        callId: incomingCall.callId,
        targetUserId: incomingCall.callerId,
        channelId: incomingCall.channelId,
      });
      setIncomingCall(null);
    }
  };

  const endCall = () => {
    stopAllCallRingtones();
    setIsOutgoingRinging(false);
    if (activeCall) {
      if (activeCall.targetUserId) {
        sendCallSignal({
          type: 'call_signal',
          action: 'call_ended',
          targetUserId: activeCall.targetUserId,
        });
      } else if (activeCall.channelId) {
        sendCallSignal({
          type: 'call_signal',
          action: 'call_ended',
          channelId: activeCall.channelId,
        });
      }
    }
    cleanupMedia();
    setActiveCall(null);
    setIncomingCall(null);
  };

  const toggleAudio = () => {
    setActiveCall((prev) => {
      if (!prev) return null;
      const nextMuted = !prev.isAudioMuted;

      if (localStreamRef.current) {
        localStreamRef.current.getAudioTracks().forEach((track) => {
          track.enabled = !nextMuted;
        });
      }

      const myId = user?.id || 'usr-admin';
      const updated = prev.participants.map((p) =>
        p.id === myId ? { ...p, isMuted: nextMuted, isSpeaking: false } : p
      );

      sendCallSignal({
        type: 'call_signal',
        action: 'media_state_change',
        userId: myId,
        isMuted: nextMuted,
        targetUserId: prev.targetUserId,
        channelId: prev.channelId,
      });

      return {
        ...prev,
        isAudioMuted: nextMuted,
        participants: updated,
      };
    });
  };

  // Helper to attach/replace video track and renegotiate WebRTC offer if needed (e.g. upgrading Audio call to Video)
  const attachVideoTrackToPeers = async (videoTrack: MediaStreamTrack, streamForTrack: MediaStream) => {
    for (const [peerId, pc] of peerConnectionsRef.current.entries()) {
      const videoSender = pc.getSenders().find((s) => s.track?.kind === 'video' || (s as any).kind === 'video');
      if (videoSender) {
        await videoSender.replaceTrack(videoTrack).catch((err) => console.warn('Failed to replace video track:', err));
      } else {
        pc.addTrack(videoTrack, streamForTrack);
        try {
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          sendCallSignal({
            type: 'call_signal',
            action: 'webrtc_offer',
            sdp: offer,
            targetUserId: peerId,
          });
        } catch (err) {
          console.warn('WebRTC renegotiation error when adding video track:', err);
        }
      }
    }
  };

  const toggleVideo = async () => {
    if (!activeCall) return;
    const nextVideoOff = !activeCall.isVideoOff;

    if (localStreamRef.current) {
      const videoTracks = localStreamRef.current.getVideoTracks();
      if (videoTracks.length === 0 && !nextVideoOff) {
        try {
          const camStream = await navigator.mediaDevices.getUserMedia({
            video: { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } },
          });
          const camTrack = camStream.getVideoTracks()[0];
          if (camTrack) {
            localStreamRef.current.addTrack(camTrack);
            const updatedStream = new MediaStream(localStreamRef.current.getTracks());
            setLocalStream(updatedStream);
            setHasCamera(true);
            await attachVideoTrackToPeers(camTrack, localStreamRef.current);
          }
        } catch (err) {
          console.error('Failed to acquire camera stream:', err);
          return;
        }
      } else {
        videoTracks.forEach((track) => {
          track.enabled = !nextVideoOff;
        });
      }
    } else if (!nextVideoOff) {
      try {
        const camStream = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } },
          audio: true,
        });
        localStreamRef.current = camStream;
        setLocalStream(camStream);
        setHasCamera(true);
        const camTrack = camStream.getVideoTracks()[0];
        if (camTrack) {
          await attachVideoTrackToPeers(camTrack, camStream);
        }
      } catch (err) {
        console.error('Failed to acquire camera stream:', err);
        return;
      }
    }

    const myId = user?.id || 'usr-admin';
    setActiveCall((prev) => {
      if (!prev) return null;
      const updated = prev.participants.map((p) =>
        p.id === myId ? { ...p, isVideoOff: nextVideoOff } : p
      );
      return {
        ...prev,
        callMode: !nextVideoOff ? 'video' : prev.callMode,
        isVideoOff: nextVideoOff,
        participants: updated,
      };
    });

    sendCallSignal({
      type: 'call_signal',
      action: 'media_state_change',
      userId: myId,
      isVideoOff: nextVideoOff,
      targetUserId: activeCall.targetUserId,
      channelId: activeCall.channelId,
    });
  };

  const toggleScreenShare = async () => {
    if (!activeCall) return;

    if (activeCall.isScreenSharing) {
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach((t) => t.stop());
        screenStreamRef.current = null;
        setScreenStream(null);
      }

      // Revert video track back to camera on all peers
      const camTrack = localStreamRef.current ? localStreamRef.current.getVideoTracks()[0] || null : null;
      peerConnectionsRef.current.forEach((pc) => {
        const videoSender = pc.getSenders().find((s) => s.track?.kind === 'video' || (s as any).kind === 'video');
        if (videoSender) {
          videoSender.replaceTrack(camTrack).catch((err) => console.warn('Failed to restore camera track:', err));
        }
      });

      const myId = user?.id || 'usr-admin';
      setActiveCall((prev) => {
        if (!prev) return null;
        const updated = prev.participants.map((p) =>
          p.id === myId ? { ...p, isScreenSharing: false } : p
        );
        return {
          ...prev,
          isScreenSharing: false,
          participants: updated,
        };
      });

      sendCallSignal({
        type: 'call_signal',
        action: 'screen_share_change',
        userId: myId,
        isScreenSharing: false,
        targetUserId: activeCall.targetUserId,
        channelId: activeCall.channelId,
      });
    } else {
      if (typeof navigator !== 'undefined' && navigator.mediaDevices?.getDisplayMedia) {
        try {
          const screen = await navigator.mediaDevices.getDisplayMedia({
            video: {
              frameRate: { ideal: 30, max: 60 },
              width: { ideal: 1920, max: 2560 },
              height: { ideal: 1080, max: 1440 },
            },
            audio: false,
          });

          screenStreamRef.current = screen;
          setScreenStream(screen);

          const screenTrack = screen.getVideoTracks()[0];

          // Broadcast screen video track to all connected peers (renegotiating if call started as audio-only)
          await attachVideoTrackToPeers(screenTrack, screen);

          screenTrack.onended = () => {
            if (screenStreamRef.current) {
              screenStreamRef.current = null;
              setScreenStream(null);
            }
            const camTrack = localStreamRef.current ? localStreamRef.current.getVideoTracks()[0] || null : null;
            peerConnectionsRef.current.forEach((pc) => {
              const videoSender = pc.getSenders().find((s) => s.track?.kind === 'video' || (s as any).kind === 'video');
              if (videoSender) {
                videoSender.replaceTrack(camTrack).catch(() => {});
              }
            });
            const myId = user?.id || 'usr-admin';
            setActiveCall((prev) => {
              if (!prev) return null;
              const updated = prev.participants.map((p) =>
                p.id === myId ? { ...p, isScreenSharing: false } : p
              );
              return {
                ...prev,
                isScreenSharing: false,
                participants: updated,
              };
            });
            sendCallSignal({
              type: 'call_signal',
              action: 'screen_share_change',
              userId: myId,
              isScreenSharing: false,
              targetUserId: activeCall.targetUserId,
              channelId: activeCall.channelId,
            });
          };

          const myId = user?.id || 'usr-admin';
          setActiveCall((prev) => {
            if (!prev) return null;
            const updated = prev.participants.map((p) =>
              p.id === myId ? { ...p, isScreenSharing: true } : p
            );
            return {
              ...prev,
              isScreenSharing: true,
              participants: updated,
            };
          });

          sendCallSignal({
            type: 'call_signal',
            action: 'screen_share_change',
            userId: myId,
            isScreenSharing: true,
            targetUserId: activeCall.targetUserId,
            channelId: activeCall.channelId,
          });
        } catch (err) {
          console.warn('Screen share cancelled or failed:', err);
        }
      }
    }
  };

  const toggleFloating = () => {
    setActiveCall((prev) => (prev ? { ...prev, isFloating: !prev.isFloating } : null));
  };

  return (
    <CallContext.Provider
      value={{
        activeCall,
        incomingCall,
        localStream,
        screenStream,
        remoteStreams,
        hasCamera,
        hasMic,
        isOutgoingRinging,
        startDirectCall,
        startGroupCall,
        joinCall,
        acceptIncomingCall,
        declineIncomingCall,
        endCall,
        toggleAudio,
        toggleVideo,
        toggleScreenShare,
        toggleFloating,
      }}
    >
      {/* Dedicated background audio elements for all remote participants */}
      {Object.entries(remoteStreams).map(([peerId, stream]) => (
        <RemoteAudioPlayer key={peerId} stream={stream} />
      ))}
      {children}
    </CallContext.Provider>
  );
};

const RemoteAudioPlayer: React.FC<{ stream: MediaStream }> = ({ stream }) => {
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    const el = audioRef.current;
    if (el && stream) {
      el.srcObject = stream;
      el.play().catch((err) => {
        console.warn('Autoplay remote audio error:', err);
      });
    }
  }, [stream]);

  return <audio ref={audioRef} autoPlay playsInline />;
};

export const useCall = () => useContext(CallContext);

